/** Mașinile machetei de situație: corpuri netede, „de lut”, în alb și gri.
 *
 *  Macheta e o machetă de prezentare (clădiri albe, orașul în gri, verdele
 *  stins); o mașină colorată sau cu detalii trage ochiul de la clădire. Așa că
 *  mașina e doar o formă: un corp rotunjit, o cabină îngustată deasupra
 *  centurii și patru roți abia ieșite — fără geamuri, faruri sau oglinzi.
 *
 *  Geometria e o suprafață trasă prin secțiuni: în lungul mașinii, fiecare
 *  secțiune e o superelipsă între podea și profilul acoperișului, îngustată
 *  peste centură; capetele se închid după o superelipsă în plan. Trei variante
 *  de proporție; fiecare e O plasă instanțiată, deci tot parcajul costă trei
 *  apeluri de desen.
 *
 *  Cadrul local: lungimea pe Y, cu botul spre -Y (ca `siteGlyphs.CarBody`),
 *  lățimea pe X, Z în sus, roțile pe Z = 0. */
import * as THREE from "three";
import { culoareMasina, locOcupat, variantaLoc } from "./masiniLot";

export interface VariantaMasina {
  nume: string;
  L: number;              // lungimea, m
  W: number;              // lățimea, m
  /** Profilul acoperișului: [t, z] de la bot (t = 0) la coadă (t = 1). */
  profil: [number, number][];
  /** Centura: peste ea secțiunea se îngustează (cabina). */
  centura: number;
  roti: [number, number]; // t-ul celor două osii
}

export const VARIANTE_MASINA: VariantaMasina[] = [
  { nume: "berlina", L: 4.65, W: 1.84, centura: 0.92, roti: [0.19, 0.79],
    profil: [[0, 0.66], [0.12, 0.84], [0.28, 0.92], [0.42, 1.38], [0.50, 1.45],
             [0.66, 1.43], [0.80, 1.10], [0.92, 1.00], [1, 0.86]] },
  { nume: "suv", L: 4.70, W: 1.90, centura: 1.08, roti: [0.18, 0.80],
    profil: [[0, 0.82], [0.12, 1.02], [0.26, 1.10], [0.38, 1.58], [0.48, 1.66],
             [0.80, 1.63], [0.94, 1.48], [1, 1.12]] },
  { nume: "compacta", L: 4.15, W: 1.80, centura: 0.94, roti: [0.18, 0.82],
    profil: [[0, 0.68], [0.12, 0.86], [0.26, 0.94], [0.42, 1.42], [0.52, 1.48],
             [0.82, 1.44], [0.95, 1.20], [1, 0.92]] },
];

export const variantaMasina = (x: number, y: number) => variantaLoc(x, y, VARIANTE_MASINA.length);

const lerpProfil = (p: [number, number][], t: number) => {
  for (let i = 1; i < p.length; i++) {
    if (t <= p[i][0]) {
      const [t0, z0] = p[i - 1], [t1, z1] = p[i];
      const u = (t - t0) / (t1 - t0 || 1);
      const s = u * u * (3 - 2 * u);           // racord, nu muchie, între puncte
      return z0 + (z1 - z0) * s;
    }
  }
  return p[p.length - 1][1];
};

const NIVEL_PODEA = 0.24;   // garda la sol, la mijloc
const STATII = 30;          // secțiuni în lung
const SECTIUNE = 18;        // puncte pe secțiune

/** Caroseria unei variante, netedă (normale din vecini). */
export function geometrieCaroserie(v: VariantaMasina): THREE.BufferGeometry {
  const poz: number[] = [];
  const idx: number[] = [];
  for (let i = 0; i <= STATII; i++) {
    // stațiile se îndesesc spre capete, unde forma se închide repede
    const t = 0.5 - 0.5 * Math.cos((i / STATII) * Math.PI);
    const e = Math.abs(2 * t - 1);
    // închiderea capetelor: superelipsă în plan și în elevație
    const fPlan = Math.pow(Math.max(0, 1 - Math.pow(e, 4.5)), 1 / 4.5);
    const fSus = Math.pow(Math.max(0, 1 - Math.pow(e, 6)), 1 / 6);
    const zt = lerpProfil(v.profil, t);
    const zb = NIVEL_PODEA + 0.14 * Math.pow(e, 3);      // consolele urcă
    const zc = (zt + zb) / 2, hh = ((zt - zb) / 2) * fSus;
    const hw = (v.W / 2) * fPlan;
    const y = -v.L / 2 + t * v.L;
    for (let j = 0; j < SECTIUNE; j++) {
      const a = (j / SECTIUNE) * Math.PI * 2;
      const c = Math.cos(a), s = Math.sin(a);
      // superelipsă cu exponentul 4: flancuri aproape drepte, colțuri rotunde
      const sx = Math.sign(c) * Math.pow(Math.abs(c), 0.5);
      const sz = Math.sign(s) * Math.pow(Math.abs(s), 0.5);
      const z = zc + hh * sz;
      // peste centură, cabina se strânge (sticla e trasă înăuntru)
      const peste = Math.min(1, Math.max(0, (z - v.centura) / 0.45));
      const ingust = 1 - 0.24 * peste * peste * (3 - 2 * peste);
      poz.push(hw * sx * ingust, y, z);
    }
  }
  for (let i = 0; i < STATII; i++) {
    for (let j = 0; j < SECTIUNE; j++) {
      const a = i * SECTIUNE + j, b = i * SECTIUNE + ((j + 1) % SECTIUNE);
      const c = a + SECTIUNE, d = b + SECTIUNE;
      idx.push(a, c, b, b, c, d);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(poz, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

const R_ROATA = 0.34;
const L_ROATA = 0.22;

/** Cele patru roți, aceeași plasă cu caroseria (un singur material). */
export function geometrieMasina(v: VariantaMasina): THREE.BufferGeometry {
  const parti: THREE.BufferGeometry[] = [geometrieCaroserie(v)];
  for (const t of v.roti) {
    for (const sx of [-1, 1]) {
      const r = new THREE.CylinderGeometry(R_ROATA, R_ROATA, L_ROATA, 14, 1);
      r.rotateZ(Math.PI / 2);                     // axa pe X
      // roata intră pe jumătate în caroserie: se vede ca o roată în pasaj, nu ca un disc lipit
      r.translate(sx * (v.W / 2 - L_ROATA / 2 - 0.07), -v.L / 2 + t * v.L, R_ROATA);
      parti.push(r.toNonIndexed());
    }
  }
  // caroseria indexată + roțile neindexate: se aduc toate la neindexat
  const toate = parti.map((p) => (p.index ? p.toNonIndexed() : p));
  const n = toate.reduce((s, p) => s + p.attributes.position.count, 0);
  const pos = new Float32Array(n * 3), nor = new Float32Array(n * 3);
  let o = 0;
  for (const p of toate) {
    pos.set(p.attributes.position.array as Float32Array, o * 3);
    nor.set(p.attributes.normal.array as Float32Array, o * 3);
    o += p.attributes.position.count;
  }
  parti.forEach((p) => p.dispose());
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  g.setAttribute("normal", new THREE.BufferAttribute(nor, 3));
  return g;
}

/** Un loc de parcare: centrul, unghiul (grade) și gabaritul locului. */
export interface LocMasina { cx: number; cy: number; deg: number; w: number; d: number }

/** Mașinile pe locuri: o plasă instanțiată pe variantă, nuanța pe instanță.
 *  Mașina se micșorează uniform cât să lase ~0,4 m de asfalt pe margini. */
export function construiesteMasini(locuri: readonly LocMasina[], { umbre = true } = {}) {
  const material = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.5, metalness: 0 });
  const pe: LocMasina[][] = VARIANTE_MASINA.map(() => []);
  for (const l of locuri) if (locOcupat(l.cx, l.cy)) pe[variantaMasina(l.cx, l.cy)].push(l);
  const obiecte: THREE.Object3D[] = [];
  const deArs: { dispose(): void }[] = [material];
  const t = new THREE.Object3D(), cul = new THREE.Color();
  VARIANTE_MASINA.forEach((v, k) => {
    const lista = pe[k];
    if (!lista.length) return;
    const g = geometrieMasina(v);
    deArs.push(g);
    const im = new THREE.InstancedMesh(g, material, lista.length);
    im.name = `masini:${v.nume}`;
    im.castShadow = umbre;
    im.receiveShadow = true;
    lista.forEach((l, i) => {
      const s = Math.min(1, (l.d - 0.4) / v.L, (l.w - 0.35) / v.W);
      t.position.set(l.cx, l.cy, 0);
      // botul spre fundul locului (parcat cu fața), ca `siteGlyphs.SiteBay`:
      // modelul are botul spre -Y, locul iese spre -Y, deci încă o jumătate de tură
      t.rotation.set(0, 0, (l.deg * Math.PI) / 180 + Math.PI);
      t.scale.setScalar(Math.max(0.5, s));
      t.updateMatrix();
      im.setMatrixAt(i, t.matrix);
      im.setColorAt(i, cul.set(culoareMasina(l.cx, l.cy)));
    });
    im.instanceMatrix.needsUpdate = true;
    if (im.instanceColor) im.instanceColor.needsUpdate = true;
    // nu se aleg cu mouse-ul: sunt decor, ca pomii
    im.raycast = () => {};
    obiecte.push(im);
  });
  return { obiecte, deArs, numar: pe.reduce((s, l) => s + l.length, 0) };
}
